import { Link, Stack, Tooltip, Typography } from "@mui/material";
import {
  GetFrameRate,
  GetIsFileTCDisplayed,
  GetRealCurrentFrame,
  GetTimecodeIn,
  GoToFrame,
  ReactPlayerMarker,
} from "../../Redux/Reducers/redux-slice-react-player";
import { useAppDispatch, useAppSelector } from "../../Redux/redux-store";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import React from "react";

export const defaultTimecode: string = ""; // 00:00:00:00

export const SecToTimecode = function (s: number, fps: number): string {
  let frames: string | number = parseInt(Math.round((s * fps) % fps).toString());
  if (frames >= fps) frames = 0;
  if (frames === 0) s = Math.round(s);
  let seconds: string | number = parseInt((s % 60).toString());
  let minutes: string | number = parseInt(((s % 3600) / 60).toString());
  let hours: string | number = parseInt(((s % 86400) / 3600).toString());

  hours = hours < 10 ? "0" + hours : hours;
  minutes = minutes < 10 ? "0" + minutes : minutes;
  seconds = seconds < 10 ? "0" + seconds : seconds;
  frames = frames < 10 ? "0" + frames : frames;

  return hours + ":" + minutes + ":" + seconds + ":" + frames;
};

export const FrameToTimecode = function (frame: number, fps: number): string {
  if (frame === 0) return "00:00:00:00";

  let seconds: string | number = Math.floor(frame / fps) % 60;
  let minutes: string | number = Math.floor(frame / (fps * 60)) % 60;
  let hours: string | number = Math.floor(frame / (fps * 3600));
  let frames: string | number = Math.floor(frame % fps);

  hours = hours < 10 ? "0" + hours : hours;
  minutes = minutes < 10 ? "0" + minutes : minutes;
  seconds = seconds < 10 ? "0" + seconds : seconds;
  frames = frames < 10 ? "0" + frames : frames;

  return hours + ":" + minutes + ":" + seconds + ":" + frames;
};

export const TimecodeToSec = function (t: string | null, fps: number | null): number {
  if (t === null || t === "" || t === undefined || fps === null) return 0;
  let numbers = t.split(":");

  let hoursInSeconds = parseInt(numbers[0]) * 3600;
  let minutesInSeconds = parseInt(numbers[1]) * 60;
  let seconds = parseInt(numbers[2]);
  let framesInSeconds = parseFloat((parseInt(numbers[3]) / fps).toString());

  return hoursInSeconds + minutesInSeconds + seconds + framesInSeconds;
};

export const TimecodeToFrame = function (t: string, fps: number): number {
  if (!t || t === null || t === "" || fps === null) return 0;
  let numbers = t.split(":");

  let h = parseInt(numbers[0]);
  let m = parseInt(numbers[1]);
  let s = parseInt(numbers[2]);
  let f = parseInt(numbers[3]);

  let fph = fps * 3600;
  let fpm = fps * 60;

  let frameNumber = fph * h + fpm * m + fps * s + f;

  return frameNumber;
};

export const SecToFrame = function (s: number, fps: number, isForBackend: boolean = false): number {
  let frameNumber = fps * s;
  return Math.round(frameNumber) - (isForBackend ? 1 : 0);
};

export const FrameToMs = function (frame: number, fps: number): string {
  return Math.floor((frame / fps) * 1000)
    .toString()
    .padStart(3, "0");
};

export const TimecodeToFileTimecode = function (timecodeIn: string | undefined, timecode: string, frameRate): string {
  // Turn any timecode into a "file timecode" version, which is a file that starts at a given timecodeIn instead of 00:00:00:00
  if (!timecodeIn) return timecode;
  const startOffset = TimecodeToSec(timecodeIn, frameRate);
  const secNumber = TimecodeToSec(timecode, frameRate);
  const fileTimecodeSecNumber = secNumber + startOffset;
  return SecToTimecode(fileTimecodeSecNumber, frameRate);
};

export const FileTimecodeToTimecode = function (timecodeIn: string | undefined, timecode: string, frameRate): string {
  if (!timecodeIn) return timecode;
  const startOffset = TimecodeToSec(timecodeIn, frameRate);
  const secNumber = TimecodeToSec(timecode, frameRate);
  const timecodeSecNumber = secNumber - startOffset;
  return SecToTimecode(timecodeSecNumber, frameRate);
};

export function checkTimecode(timecode: string, frameRate?: number): boolean {
  var regexTimecode;
  if (frameRate === 29.97)
    regexTimecode = new RegExp("(?:[01]\\d|2[0123]):(?:[012345]\\d):(?:[012345]\\d):(?:[012]\\d)");
  else regexTimecode = new RegExp("(?:[01]\\d|2[0123]):(?:[012345]\\d):(?:[012345]\\d):(?:[01]\\d|2[01234])");
  return regexTimecode.test(timecode);
}

export function CreateMarker(frame: number): ReactPlayerMarker {
  let result: ReactPlayerMarker = { markFrame: 0, isSet: true };
  result.markFrame = frame;

  return result;
}

interface JWPMarkerTimecodeProps extends ReactPlayerMarker {
  runOnclick?: (x: number) => void;
  frameRate: number;
}

export const JWPMarkerTimecode: React.FC<JWPMarkerTimecodeProps> = (props) => {
  const dispatch = useAppDispatch();
  const { markFrame } = { ...props };
  const { frameRate } = props;
  const isFileTCDisplayed = useAppSelector(GetIsFileTCDisplayed);
  const timecodeIn = useAppSelector(GetTimecodeIn);
  //   const frameRate = useAppSelector(GetFrameRate);
  return (
    <Tooltip
      title={
        <Typography fontWeight={500} variant="body1" color="text.tier">
          Frame #{markFrame}
        </Typography>
      }
      arrow
    >
      <Link
        variant="body1"
        color="primary.text"
        underline="hover"
        onClick={(e) => {
          e.stopPropagation();
          props.runOnclick ? props.runOnclick(markFrame) : dispatch(GoToFrame(markFrame));
        }}
      >
        {isFileTCDisplayed
          ? TimecodeToFileTimecode(timecodeIn, FrameToTimecode(markFrame, frameRate), frameRate)
          : FrameToTimecode(markFrame, frameRate)}
        {/* {isFileTCDisplayed ? markTimecodeFile || markTimecode : markTimecode} */}
      </Link>
    </Tooltip>
  );
};

export const PlayerTimeDuration: React.FC = () => {
  const playerMediaDuration = useAppSelector(GetRealCurrentFrame) / useAppSelector(GetFrameRate) || 0;

  return React.useMemo(() => {
    let minutes = Math.floor(playerMediaDuration / 60);
    let hours = Math.floor(minutes / 60);
    minutes = minutes % 60;

    let duration: string =
      hours > 0 ? hours.toString() + " H " + minutes.toString() + " min" : minutes.toString() + " min";

    return (
      <Stack direction="row" alignItems="center" spacing={0.5} color="text.tier">
        <AccessTimeOutlinedIcon fontSize="small" />
        <Typography>{duration}</Typography>
      </Stack>
    );
  }, [playerMediaDuration]);
};
